/**
 * Pick a FusionDocs folder: type a path (a new one is fine) or choose from the existing folders, whose children
 * load on demand. Used by Move (preview) and the Upload form.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { ChevronRight, Folder, FolderInput, Loader2 } from "lucide-react";
import { useState } from "react";
import { api } from "../../lib/api";
import { Modal } from "../ui";
import { afterEdit, cleanPath, editErrText, errText, FK, folderUrl, patchDoc, type FdFolder, type FdFolders } from "./fd";

export function FolderPicker({ value, onChange, collapsed = false, autoFocus }: { value: string; onChange: (v: string) => void; collapsed?: boolean; autoFocus?: boolean }) {
  const [show, setShow] = useState(!collapsed);
  return (
    <div>
      <div className="flex gap-2">
        <input
          className="input min-w-0 flex-1"
          value={value}
          maxLength={500}
          autoFocus={autoFocus}
          placeholder="e.g. Finance/Invoices/2026"
          aria-label="Folder path"
          onChange={(e) => onChange(e.target.value)}
        />
        {collapsed && (
          <button type="button" className="btn btn-sm shrink-0" aria-expanded={show} onClick={() => setShow((s) => !s)}>
            {show ? "Hide folders" : "Browse"}
          </button>
        )}
      </div>
      {show && <FolderList value={cleanPath(value)} onPick={onChange} />}
    </div>
  );
}

function FolderList({ value, onPick }: { value: string; onPick: (p: string) => void }) {
  const top = useQuery({ queryKey: FK.folders(""), queryFn: () => api<FdFolders>(folderUrl()), staleTime: 5 * 60_000, retry: false });
  return (
    <div className="scroll-thin mt-2 max-h-56 overflow-auto rounded-lg border border-[var(--line)] p-1 text-sm">
      {top.isLoading ? <p className="muted flex items-center gap-1.5 p-2 text-xs"><Loader2 size={12} className="animate-spin" /> Loading folders…</p>
        : top.error ? <p className="p-2 text-xs text-red-700">Couldn't load folders: {errText(top.error)}</p>
        : (top.data?.items.length ?? 0) === 0 ? <p className="muted p-2 text-xs">No folders yet. Type a path above to start the first one.</p>
        : <ul role="tree">{top.data!.items.map((f) => <PickNode key={f.path} folder={f} depth={0} value={value} onPick={onPick} />)}</ul>}
    </div>
  );
}

function PickNode({ folder, depth, value, onPick }: { folder: FdFolder; depth: number; value: string; onPick: (p: string) => void }) {
  const [open, setOpen] = useState(!!value && value.startsWith(`${folder.path}/`));
  const canOpen = folder.hasChildren !== false;
  const kids = useQuery({
    queryKey: FK.folders(folder.path),
    queryFn: () => api<FdFolders>(folderUrl(folder.path)),
    enabled: open && canOpen,
    staleTime: 5 * 60_000,
  });
  const active = value === folder.path;
  return (
    <li role="treeitem" aria-selected={active} aria-expanded={canOpen ? open : undefined}>
      <div className={clsx("flex items-center rounded-md", active ? "bg-brand-50 text-brand-700" : "hover:bg-[var(--panel-2)]")} style={{ paddingLeft: depth * 12 }}>
        <button type="button" className={clsx("grid h-7 w-6 shrink-0 place-items-center", !canOpen && "invisible")} tabIndex={canOpen ? 0 : -1}
          onClick={() => setOpen((o) => !o)} aria-label={open ? `Close ${folder.name}` : `Open ${folder.name}`}>
          <ChevronRight size={14} className={clsx("muted transition-transform", open && "rotate-90")} />
        </button>
        <button type="button" className="flex min-w-0 flex-1 items-center gap-2 py-1.5 pr-2 text-left" onClick={() => { onPick(folder.path); if (canOpen) setOpen(true); }}>
          <Folder size={14} className={clsx("shrink-0", active ? "text-brand-600" : "muted")} aria-hidden />
          <span className="min-w-0 flex-1 truncate">{folder.name}</span>
          {folder.count != null && <span className="muted shrink-0 text-xs num">{folder.count}</span>}
        </button>
      </div>
      {open && canOpen && (kids.isLoading
        ? <p className="muted flex items-center gap-1.5 py-1 text-xs" style={{ paddingLeft: depth * 12 + 30 }}><Loader2 size={12} className="animate-spin" /> Loading…</p>
        : (kids.data?.items.length ?? 0) > 0 && (
          <ul role="group">{kids.data!.items.map((k) => <PickNode key={k.path} folder={k} depth={depth + 1} value={value} onPick={onPick} />)}</ul>
        ))}
    </li>
  );
}

export function MoveDialog({ id, title, current, onClose }: { id: string; title: string; current?: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [path, setPath] = useState(current ?? "");
  const target = cleanPath(path);
  const move = useMutation({
    mutationFn: () => patchDoc(id, { filingPath: target }),
    onSuccess: (r) => { afterEdit(qc, id, r.document); onClose(); },
  });
  return (
    <Modal title="Move to a folder" onClose={() => !move.isPending && onClose()}
      footer={<>
        <button className="btn" onClick={onClose} disabled={move.isPending}>Cancel</button>
        <button className="btn btn-primary" disabled={move.isPending || !target || target === cleanPath(current ?? "")} onClick={() => move.mutate()}>
          {move.isPending ? <Loader2 size={14} className="animate-spin" /> : <FolderInput size={14} />} Move
        </button>
      </>}>
      <p className="truncate text-sm font-semibold" title={title}>{title}</p>
      <p className="muted -mt-2 text-xs">Now in: {current || "Not yet filed"}</p>
      <form onSubmit={(e) => { e.preventDefault(); if (target && !move.isPending) move.mutate(); }}>
        <FolderPicker value={path} onChange={(v) => { setPath(v); move.reset(); }} autoFocus />
      </form>
      <p className="muted text-xs">Pick a folder, or type a new path. FusionDocs checks the location and may adjust it.</p>
      {move.error && <p role="alert" className="text-sm text-red-700">{editErrText(move.error)}</p>}
    </Modal>
  );
}
